import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { 
    Trophy, 
    Target, 
    Zap, 
    BookOpen, 
    Users, 
    TrendingUp,
    ChevronRight,
    Brain,
    Award,
    Clock,
    CheckCircle2,
    XCircle,
    UserPlus,
    Sparkles
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { demoAPI } from "../lib/api";
import { toast } from "sonner";

const AMERIBAR_LOGO = "/logo.png";

// Brand component for consistent usage
const BrandLogo = ({ size = "default" }) => (
    <div className="flex items-center gap-2">
        <span className={`font-heading font-black text-white ${size === "large" ? "text-2xl" : "text-xl"}`}>
            BarExam<span className="text-[#299dce]">Game</span><span className="text-slate-400">.com</span>
        </span>
    </div>
);

const features = [
    {
        icon: Target,
        title: "Trivia Battles",
        description: "Test your knowledge with timed multiple-choice questions across all 8 UBE subjects.",
        color: "text-[#299dce]"
    },
    {
        icon: Trophy,
        title: "Leaderboards",
        description: "Compete with law students nationwide. Climb the ranks and prove your mastery.",
        color: "text-[#299dce]"
    },
    {
        icon: Brain,
        title: "AI-Powered Questions",
        description: "Fresh, MBE-style questions generated by advanced AI to keep you challenged.",
        color: "text-[#299dce]"
    },
    {
        icon: BookOpen,
        title: "Study Modes",
        description: "Flashcards and practice mode for focused learning without the pressure.",
        color: "text-emerald-400"
    },
    {
        icon: Award,
        title: "Achievements",
        description: "Unlock badges and rewards as you progress. Every milestone celebrated.",
        color: "text-amber-400"
    },
    {
        icon: TrendingUp,
        title: "Progress Tracking",
        description: "Detailed analytics on your performance across subjects and over time.",
        color: "text-[#299dce]"
    }
];

const QUESTION_TIME = 30;

export default function LandingPage() {
    const { user } = useAuth();
    const navigate = useNavigate();
    
    // Demo game state
    const [gameState, setGameState] = useState("ready"); // ready, playing, result, signup
    const [sessionId, setSessionId] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selectedOption, setSelectedOption] = useState(null);
    const [answerResult, setAnswerResult] = useState(null);
    const [timeLeft, setTimeLeft] = useState(QUESTION_TIME);
    const [startTime, setStartTime] = useState(null);
    const [score, setScore] = useState(0);
    const [correctCount, setCorrectCount] = useState(0);
    const [loading, setLoading] = useState(true);
    
    // Load first question on page load
    useEffect(() => {
        if (!user) {
            loadDemoQuestion();
        }
    }, [user]);
    
    // Timer logic
    useEffect(() => {
        if (gameState !== "playing" || answerResult) return;
        
        if (timeLeft <= 0) {
            submitAnswer(null);
            return;
        }
        
        const timer = setTimeout(() => {
            setTimeLeft(prev => prev - 1);
        }, 1000);
        
        return () => clearTimeout(timer);
    }, [timeLeft, gameState, answerResult]);
    
    const loadDemoQuestion = async () => {
        try {
            const response = await demoAPI.startDemo();
            if (response.data.questions && response.data.questions.length > 0) {
                setSessionId(response.data.session_id);
                setQuestions(response.data.questions);
                setCurrentIndex(0);
                setGameState("ready");
            }
        } catch (error) {
            console.error("Failed to load demo:", error);
        } finally {
            setLoading(false);
        }
    };
    
    const startPlaying = (optionId) => {
        setStartTime(Date.now());
        setGameState("playing");
        submitAnswer(optionId);
    };
    
    const submitAnswer = async (optionId) => {
        if (!sessionId || !questions[currentIndex]) {
            console.error("Missing sessionId or question", { sessionId, currentIndex, questions: questions.length });
            return;
        }
        
        const timeTaken = startTime ? (Date.now() - startTime) / 1000 : QUESTION_TIME;
        setSelectedOption(optionId);
        
        try {
            const response = await demoAPI.submitAnswer(sessionId, {
                question_id: questions[currentIndex].id,
                selected_option_id: optionId || "none",
                time_taken: timeTaken
            });
            
            setAnswerResult(response.data);
            setScore(prev => prev + response.data.points_earned);
            
            if (response.data.is_correct) {
                setCorrectCount(prev => prev + 1);
            }
            
            setGameState("result");
        } catch (error) {
            console.error("Submit answer error:", error);
            toast.error("Failed to submit answer");
        }
    };
    
    const nextQuestion = () => {
        if (currentIndex + 1 >= questions.length) {
            // Demo complete - show signup
            endDemo();
        } else {
            setCurrentIndex(prev => prev + 1);
            setSelectedOption(null);
            setAnswerResult(null);
            setTimeLeft(QUESTION_TIME);
            setStartTime(Date.now());
            setGameState("playing");
        }
    };
    
    const endDemo = async () => {
        try {
            await demoAPI.endDemo(sessionId);
        } catch (error) {
            console.error("Failed to end demo:", error);
        }
        setGameState("signup");
    };
    
    const currentQuestion = questions[currentIndex];
    const progressPercent = questions.length > 0 ? ((currentIndex) / questions.length) * 100 : 0;
    
    return (
        <div className="min-h-screen bg-background">
            {/* Hero Section with Playable Question */}
            <section className="relative min-h-screen flex flex-col bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
                {/* Navigation */}
                <nav className="relative z-10 px-6 py-4 border-b border-slate-800/50">
                    <div className="max-w-7xl mx-auto flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-3">
                            <BrandLogo />
                        </Link>
                        <div className="flex items-center gap-4">
                            {user ? (
                                <Link to="/dashboard">
                                    <Button data-testid="nav-dashboard-btn" className="rounded-full bg-[#299dce] hover:bg-[#299dce]/90">
                                        Dashboard
                                        <ChevronRight className="w-4 h-4 ml-1" />
                                    </Button>
                                </Link>
                            ) : (
                                <>
                                    <Link to="/login">
                                        <Button data-testid="nav-login-btn" variant="ghost" className="text-slate-300 hover:text-white">
                                            Sign In
                                        </Button>
                                    </Link>
                                    <Link to="/register">
                                        <Button data-testid="nav-register-btn" variant="outline" className="rounded-full border-[#299dce]/50 text-[#299dce] hover:bg-[#299dce]/10 hover:border-[#299dce]">
                                            Create Account
                                        </Button>
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                </nav>
                
                {/* Hero Content */}
                <div className="flex-1 flex items-center justify-center px-6 py-12">
                    <div className="max-w-6xl mx-auto w-full">
                        {user ? (
                            // Logged in user - show simple hero
                            <div className="text-center">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                >
                                    <Badge className="mb-6 bg-[#299dce]/20 text-[#299dce] border-0 px-4 py-1.5 text-sm font-mono uppercase tracking-widest">
                                        Welcome Back
                                    </Badge>
                                    <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6">
                                        Ready to <span className="text-[#299dce]">Dominate</span>?
                                    </h1>
                                    <Link to="/dashboard">
                                        <Button size="lg" className="rounded-full bg-[#299dce] hover:bg-[#299dce]/90 text-lg px-8 h-14">
                                            <Zap className="w-5 h-5 mr-2" />
                                            Go to Dashboard
                                        </Button>
                                    </Link>
                                </motion.div>
                            </div>
                        ) : (
                            // Non-logged in user - show playable question
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                                {/* Left side - Headline */}
                                <motion.div
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="text-center lg:text-left"
                                >
                                    <Badge className="mb-4 bg-[#299dce]/20 text-[#299dce] border border-[#299dce]/30 px-4 py-1.5 text-sm font-mono uppercase tracking-widest">
                                        Bar Exam Prep • Gamified
                                    </Badge>
                                    <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 tracking-tight">
                                        Master the Bar Exam
                                        <span className="block text-[#299dce]">One Question at a Time</span>
                                    </h1>
                                    <p className="text-slate-400 text-base sm:text-lg mb-6 max-w-lg mx-auto lg:mx-0">
                                        Jump right in. Answer the question to start your free trial - no signup required.
                                    </p>
                                    
                                    {gameState !== "signup" && (
                                        <div className="flex flex-wrap gap-2 justify-center lg:justify-start">
                                            {["Constitutional Law", "Contracts", "Torts", "Evidence"].map((subject) => (
                                                <span
                                                    key={subject}
                                                    className="px-3 py-1 rounded-full bg-slate-800/60 border border-slate-700/50 text-slate-400 text-xs"
                                                >
                                                    {subject}
                                                </span>
                                            ))}
                                            <span className="px-3 py-1 rounded-full bg-slate-800/60 border border-slate-700/50 text-slate-400 text-xs">
                                                +4 more
                                            </span>
                                        </div>
                                    )}
                                </motion.div>
                                
                                {/* Right side - Live Question */}
                                <motion.div
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: 0.2 }}
                                >
                                    <AnimatePresence mode="wait">
                                        {loading ? (
                                            <Card className="bg-slate-900/95 border-2 border-[#299dce]/40 rounded-2xl shadow-[0_0_40px_rgba(41,157,206,0.2)]">
                                                <CardContent className="p-8 flex items-center justify-center min-h-[400px]">
                                                    <div className="w-8 h-8 border-4 border-[#299dce] border-t-transparent rounded-full animate-spin" />
                                                </CardContent>
                                            </Card>
                                        ) : gameState === "signup" ? (
                                            // Signup prompt
                                            <motion.div
                                                key="signup"
                                                initial={{ opacity: 0, scale: 0.95 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                            >
                                                <Card className="bg-slate-900/95 border-2 border-[#299dce]/40 rounded-2xl shadow-[0_0_40px_rgba(41,157,206,0.2)]" data-testid="signup-prompt">
                                                    <CardContent className="p-6">
                                                        <div className="text-center mb-6">
                                                            <div className="w-16 h-16 rounded-full bg-[#299dce]/20 flex items-center justify-center mx-auto mb-4">
                                                                <Trophy className="w-8 h-8 text-[#299dce]" />
                                                            </div>
                                                            <h3 className="font-heading text-2xl font-bold text-white mb-1">
                                                                Nice Work!
                                                            </h3>
                                                            <p className="font-heading text-4xl font-black text-[#299dce]">
                                                                {score} pts
                                                            </p>
                                                            <p className="text-slate-400 text-sm mt-1">
                                                                {correctCount}/{questions.length} correct
                                                            </p>
                                                        </div>
                                                        
                                                        <div className="space-y-3 mb-6">
                                                            <div className="flex items-center gap-2 text-sm text-slate-300">
                                                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                                                <span>150+ bar exam questions</span>
                                                            </div>
                                                            <div className="flex items-center gap-2 text-sm text-slate-300">
                                                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                                                <span>Track progress & earn achievements</span>
                                                            </div>
                                                            <div className="flex items-center gap-2 text-sm text-slate-300">
                                                                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                                                                <span>Compete on the leaderboard</span>
                                                            </div>
                                                        </div>
                                                        
                                                        <Link to="/register" className="block">
                                                            <Button
                                                                size="lg"
                                                                className="w-full rounded-full bg-[#299dce] hover:bg-[#299dce]/90"
                                                                data-testid="hero-signup-btn"
                                                            >
                                                                <UserPlus className="w-5 h-5 mr-2" />
                                                                Create Free Account
                                                            </Button>
                                                        </Link>
                                                        <Link to="/login" className="block mt-3">
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                className="w-full text-slate-400 hover:text-white"
                                                            >
                                                                Already have an account? Sign In
                                                            </Button>
                                                        </Link>
                                                    </CardContent>
                                                </Card>
                                            </motion.div>
                                        ) : currentQuestion ? (
                                            // Active question
                                            <motion.div
                                                key={`question-${currentIndex}`}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                            >
                                                <Card className="bg-slate-900/95 border-2 border-[#299dce]/40 rounded-2xl shadow-[0_0_40px_rgba(41,157,206,0.2)]" data-testid="hero-question-card">
                                                    <CardContent className="p-6">
                                                        {/* Progress & Timer */}
                                                        <div className="flex items-center justify-between mb-4">
                                                            <div className="flex items-center gap-2">
                                                                <Badge className="bg-[#299dce]/20 text-[#299dce] border border-[#299dce]/30 text-xs font-semibold">
                                                                    {currentQuestion.subject}
                                                                </Badge>
                                                                {gameState !== "ready" && (
                                                                    <span className="text-slate-400 text-xs font-medium">
                                                                        {currentIndex + 1}/5
                                                                    </span>
                                                                )}
                                                            </div>
                                                            {gameState === "playing" && (
                                                                <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold ${
                                                                    timeLeft <= 10 ? "bg-red-500/20 text-red-400" : "bg-slate-800 text-slate-300"
                                                                }`}>
                                                                    <Clock className="w-3 h-3" />
                                                                    <span className="font-mono">{timeLeft}s</span>
                                                                </div>
                                                            )}
                                                            {score > 0 && (
                                                                <span className="font-mono text-sm text-[#299dce] font-bold">
                                                                    {score} pts
                                                                </span>
                                                            )}
                                                        </div>
                                                        
                                                        {gameState !== "ready" && (
                                                            <Progress value={progressPercent} className="h-1.5 bg-slate-700 mb-4" />
                                                        )}
                                                        
                                                        {/* Question */}
                                                        <p className="text-white text-base sm:text-lg leading-relaxed mb-5 font-medium" data-testid="hero-question-text">
                                                            {currentQuestion.text}
                                                        </p>
                                                        
                                                        {/* Options */}
                                                        <div className="space-y-3">
                                                            {currentQuestion.options.map((option) => {
                                                                const isSelected = selectedOption === option.id;
                                                                const isCorrect = answerResult?.correct_option_id === option.id;
                                                                const isWrong = isSelected && !answerResult?.is_correct;
                                                                
                                                                let optionClass = "w-full text-left p-4 rounded-xl border-2 transition-all text-sm font-medium ";
                                                                if (gameState === "result") {
                                                                    if (isCorrect) {
                                                                        optionClass += "border-emerald-500 bg-emerald-500/20 text-white";
                                                                    } else if (isWrong) {
                                                                        optionClass += "border-red-500 bg-red-500/20 text-white";
                                                                    } else {
                                                                        optionClass += "border-slate-600 bg-slate-800/50 text-slate-400";
                                                                    }
                                                                } else {
                                                                    optionClass += "border-slate-600 bg-slate-800/70 text-white hover:border-[#299dce] hover:bg-[#299dce]/10 hover:shadow-[0_0_20px_rgba(41,157,206,0.2)] cursor-pointer";
                                                                }
                                                                
                                                                return (
                                                                    <motion.button
                                                                        key={option.id}
                                                                        className={optionClass}
                                                                        onClick={() => {
                                                                            if (gameState === "ready") {
                                                                                startPlaying(option.id);
                                                                            } else if (gameState === "playing") {
                                                                                submitAnswer(option.id);
                                                                            }
                                                                        }}
                                                                        disabled={gameState === "result"}
                                                                        whileHover={gameState !== "result" ? { scale: 1.01 } : {}}
                                                                        whileTap={gameState !== "result" ? { scale: 0.99 } : {}}
                                                                        data-testid={`hero-option-${option.id}`}
                                                                    >
                                                                        <div className="flex items-start gap-3">
                                                                            <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center font-mono text-sm font-bold ${
                                                                                isCorrect ? "bg-emerald-500 text-white" :
                                                                                isWrong ? "bg-red-500 text-white" :
                                                                                "bg-[#299dce]/20 text-[#299dce] border border-[#299dce]/30"
                                                                            }`}>
                                                                                {option.id}
                                                                            </span>
                                                                            <span className="flex-1 pt-1">{option.text}</span>
                                                                            {isCorrect && <CheckCircle2 className="w-5 h-5 text-emerald-400 flex-shrink-0" />}
                                                                            {isWrong && <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />}
                                                                        </div>
                                                                    </motion.button>
                                                                );
                                                            })}
                                                        </div>
                                                        
                                                        {/* Result explanation */}
                                                        {gameState === "result" && answerResult && (
                                                            <motion.div
                                                                initial={{ opacity: 0, y: 10 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                className="mt-4"
                                                            >
                                                                <div className={`p-4 rounded-xl text-sm ${
                                                                    answerResult.is_correct 
                                                                        ? "bg-emerald-500/15 border border-emerald-500/40" 
                                                                        : "bg-red-500/15 border border-red-500/40"
                                                                }`}>
                                                                    <div className="flex items-center gap-2 mb-2">
                                                                        {answerResult.is_correct ? (
                                                                            <>
                                                                                <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                                                                                <span className="font-bold text-emerald-400">Correct! +{answerResult.points_earned}</span>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <XCircle className="w-5 h-5 text-red-400" />
                                                                                <span className="font-bold text-red-400">Incorrect</span>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                    <p className="text-slate-300 text-xs leading-relaxed line-clamp-3">
                                                                        {answerResult.explanation}
                                                                    </p>
                                                                </div>
                                                                
                                                                <Button
                                                                    onClick={nextQuestion}
                                                                    className="w-full mt-3 rounded-full bg-[#299dce] hover:bg-[#299dce]/90"
                                                                    data-testid="hero-next-btn"
                                                                >
                                                                    {currentIndex + 1 >= questions.length ? "See Results" : "Next Question"}
                                                                    <ChevronRight className="w-4 h-4 ml-1" />
                                                                </Button>
                                                            </motion.div>
                                                        )}
                                                        
                                                        {/* Ready state hint */}
                                                        {gameState === "ready" && (
                                                            <motion.div 
                                                                initial={{ opacity: 0 }}
                                                                animate={{ opacity: 1 }}
                                                                transition={{ delay: 0.5 }}
                                                                className="text-center mt-5 py-3 px-4 rounded-xl bg-[#299dce]/10 border border-[#299dce]/30"
                                                            >
                                                                <p className="text-[#299dce] text-sm font-medium">
                                                                    <Sparkles className="w-4 h-4 inline mr-2" />
                                                                    Click any answer to start playing instantly
                                                                </p>
                                                            </motion.div>
                                                        )}
                                                    </CardContent>
                                                </Card>
                                            </motion.div>
                                        ) : null}
                                    </AnimatePresence>
                                </motion.div>
                            </div>
                        )}
                    </div>
                </div>
            </section>
            
            {/* Features Section */}
            <section className="py-24 px-6 bg-slate-950">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                            Everything You Need to Pass the Bar
                        </h2>
                        <p className="text-slate-400 text-base max-w-2xl mx-auto">
                            Built by AmeriBar - The Bar Exam Experts - for law students who want to study smarter.
                        </p>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, i) => (
                            <motion.div
                                key={feature.title}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.5, delay: i * 0.1 }}
                                className="glass rounded-2xl p-6 group hover:border-[#299dce]/50 transition-colors duration-300"
                            >
                                <feature.icon className={`w-10 h-10 ${feature.color} mb-4`} />
                                <h3 className="font-heading text-xl font-semibold text-white mb-2">
                                    {feature.title}
                                </h3>
                                <p className="text-slate-400 text-sm leading-relaxed">
                                    {feature.description}
                                </p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>
            
            {/* CTA Section */}
            <section className="py-24 px-6 bg-background relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-[#299dce]/5 to-transparent pointer-events-none" />
                <div className="max-w-3xl mx-auto text-center relative">
                    <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
                        Ready to Pass the Bar?
                    </h2>
                    <p className="text-slate-400 text-base mb-8">
                        Join thousands of law students who trust AmeriBar for their bar exam prep.
                    </p>
                    <Link to={user ? "/dashboard" : "/register"}>
                        <Button 
                            data-testid="cta-start-btn"
                            size="lg" 
                            className="rounded-full bg-[#299dce] hover:bg-[#299dce]/90 text-lg px-8 h-14"
                        >
                            <Zap className="w-5 h-5 mr-2" />
                            {user ? "Go to Dashboard" : "Start Your Journey"}
                        </Button>
                    </Link>
                </div>
            </section>
            
            {/* Footer */}
            <footer className="py-8 px-6 border-t border-slate-800">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                        <BrandLogo />
                        <span className="text-slate-600">|</span>
                        <div className="flex items-center gap-2">
                            <span className="text-slate-500 text-sm">by</span>
                            <img src={AMERIBAR_LOGO} alt="AmeriBar" className="h-6 object-contain" />
                        </div>
                    </div>
                    <p className="text-slate-500 text-sm">
                        © 2026 BarExamGame.com. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
}
